<script setup lang="ts">
import { ChevronLeft, ChevronRight, Expand, ImageOff } from "lucide-vue-next";

const props = defineProps<{
  images: string[];
  alt: string;
}>();

const emit = defineEmits<{
  "open-details": [index: number];
}>();

const currentIndex = ref(0);
const failedImages = ref(new Set<string>());
const pointerStartX = ref<number | null>(null);
const ignoreNextClick = ref(false);
const slideDirection = ref<"next" | "previous">("next");

const availableImages = computed(() =>
  props.images.filter(
    (image): image is string =>
      Boolean(image) && !failedImages.value.has(image),
  ),
);

const currentImage = computed(
  () => availableImages.value[currentIndex.value] ?? null,
);

const hasMultipleImages = computed(() => availableImages.value.length > 1);

const slideTransitionName = computed(() =>
  slideDirection.value === "next"
    ? "service-slide-next"
    : "service-slide-previous",
);

watch(availableImages, images => {
  if (!images.length) {
    currentIndex.value = 0;
    return;
  }

  if (currentIndex.value >= images.length) {
    currentIndex.value = images.length - 1;
  }
});

function previousPhoto() {
  if (!hasMultipleImages.value) return;

  slideDirection.value = "previous";
  currentIndex.value =
    currentIndex.value === 0
      ? availableImages.value.length - 1
      : currentIndex.value - 1;
}

function nextPhoto() {
  if (!hasMultipleImages.value) return;

  slideDirection.value = "next";
  currentIndex.value =
    currentIndex.value === availableImages.value.length - 1
      ? 0
      : currentIndex.value + 1;
}

function openDetails() {
  if (!currentImage.value) return;

  if (ignoreNextClick.value) {
    ignoreNextClick.value = false;
    return;
  }

  emit("open-details", currentIndex.value);
}

function markAsFailed(image: string) {
  const next = new Set(failedImages.value);
  next.add(image);
  failedImages.value = next;
}

function handlePointerDown(event: PointerEvent) {
  if (event.pointerType === "mouse") return;

  pointerStartX.value = event.clientX;
}

function handlePointerUp(event: PointerEvent) {
  if (event.pointerType === "mouse" || pointerStartX.value === null) return;

  const distance = event.clientX - pointerStartX.value;
  pointerStartX.value = null;

  if (Math.abs(distance) < 45) return;

  ignoreNextClick.value = true;

  if (distance > 0) {
    previousPhoto();
  } else {
    nextPhoto();
  }
}

function cancelPointerGesture() {
  pointerStartX.value = null;
}
</script>

<template>
  <div class="min-w-0">
    <div
      v-if="currentImage"
      class="group relative aspect-[16/10] w-full touch-pan-y select-none overflow-hidden bg-gray-100"
      @pointerdown="handlePointerDown"
      @pointerup="handlePointerUp"
      @pointercancel="cancelPointerGesture"
    >
      <button
        type="button"
        class="relative size-full cursor-pointer overflow-hidden text-left"
        :aria-label="$t('worker_profile.open_gallery')"
        @click="openDetails"
      >
        <Transition :name="slideTransitionName">
          <img
            :key="currentImage"
            :src="currentImage"
            :alt="hasMultipleImages ? `${alt} ${currentIndex + 1}` : alt"
            draggable="false"
            class="absolute inset-0 size-full object-cover will-change-transform"
            @dragstart.prevent
            @error="markAsFailed(currentImage)"
          />
        </Transition>

        <span
          class="pointer-events-none absolute inset-0 z-10 bg-gradient-to-t from-black/20 via-transparent to-black/5"
        />
      </button>

      <template v-if="hasMultipleImages">
        <button
          type="button"
          class="absolute left-3 top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white shadow-sm backdrop-blur-sm transition hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
          :aria-label="$t('worker_profile.previous_photo')"
          @pointerdown.stop
          @pointerup.stop
          @click.stop="previousPhoto"
        >
          <ChevronLeft class="size-5" />
        </button>

        <button
          type="button"
          class="absolute right-3 top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white shadow-sm backdrop-blur-sm transition hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
          :aria-label="$t('worker_profile.next_photo')"
          @pointerdown.stop
          @pointerup.stop
          @click.stop="nextPhoto"
        >
          <ChevronRight class="size-5" />
        </button>

        <span
          class="pointer-events-none absolute bottom-3 left-3 z-20 rounded-lg bg-black/65 px-2.5 py-1.5 text-xs font-semibold text-white backdrop-blur-sm"
        >
          {{ currentIndex + 1 }} / {{ availableImages.length }}
        </span>
      </template>

      <button
        type="button"
        class="absolute bottom-3 right-3 z-20 flex size-9 items-center justify-center rounded-full bg-black/60 text-white shadow-sm backdrop-blur-sm transition hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        :aria-label="$t('worker_profile.open_gallery')"
        @pointerdown.stop
        @pointerup.stop
        @click.stop="openDetails"
      >
        <Expand class="size-4" />
      </button>
    </div>

    <div
      v-else
      class="flex aspect-[16/10] w-full items-center justify-center bg-gradient-to-br from-gray-50 to-primary-50 text-app-placeholder"
    >
      <div class="text-center">
        <ImageOff class="mx-auto size-7" />
        <p class="mt-2 text-xs font-semibold">
          {{ $t("worker_profile.no_service_photos") }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.service-slide-next-enter-active,
.service-slide-next-leave-active,
.service-slide-previous-enter-active,
.service-slide-previous-leave-active {
  transition: transform 300ms cubic-bezier(0.22, 1, 0.36, 1);
}

.service-slide-next-enter-from {
  transform: translateX(100%);
}

.service-slide-next-leave-to {
  transform: translateX(-100%);
}

.service-slide-previous-enter-from {
  transform: translateX(-100%);
}

.service-slide-previous-leave-to {
  transform: translateX(100%);
}

.service-slide-next-enter-to,
.service-slide-next-leave-from,
.service-slide-previous-enter-to,
.service-slide-previous-leave-from {
  transform: translateX(0);
}

@media (prefers-reduced-motion: reduce) {
  .service-slide-next-enter-active,
  .service-slide-next-leave-active,
  .service-slide-previous-enter-active,
  .service-slide-previous-leave-active {
    transition-duration: 1ms;
  }
}
</style>
